import { useEffect, useState, useRef } from 'react';
import api from '../../../services/api';
import QRCode from 'qrcode';

// 产品图标配置 - 请将图标图片放在 public/images/products/ 目录下
const productIcons = {
  'ar-glasses': '/images/products/ar-glasses.png',
  'cloud-cast': '/images/products/cloud-cast.png',
  'cloud-assistant': '/images/products/cloud-assistant.png',
  'cloud-voice': '/images/products/cloud-voice.png',
  default: '/images/products/default-product.png'
};

// 平台图标配置
const platformIcons = {
  official: '/images/platforms/official.png',
  huawei: '/images/platforms/huawei.png',
  xiaomi: '/images/platforms/xiaomi.png',
  vivo: '/images/platforms/vivo.png',
  wechat: '/images/platforms/wechat.png',
  alipay: '/images/platforms/alipay.png',
  windows: '/images/platforms/windows.png',
  linux: '/images/platforms/linux.png',
  default: '/images/platforms/default.png'
};

// 图片组件 - 处理图片加载失败的情况
const IconImage = ({ src, alt, className, fallbackIcon }: { src: string; alt: string; className: string; fallbackIcon?: string }) => {
  const [imgSrc, setImgSrc] = useState(src);
  const [hasError, setHasError] = useState(false);

  const handleError = () => {
    if (!hasError) {
      setHasError(true);
      if (fallbackIcon) {
        setImgSrc(fallbackIcon);
      }
    }
  };

  if (hasError && !fallbackIcon) {
    return <span className={className} style={{ fontSize: '32px' }}>{fallbackIcon || '📦'}</span>;
  }

  return (
    <img 
      src={imgSrc} 
      alt={alt} 
      className={className}
      onError={handleError}
    />
  );
};

// 自动生成二维码组件
const AutoQRCode = ({ url, size = 140 }: { url: string; size?: number }) => {
  const [qrDataUrl, setQrDataUrl] = useState<string>('');

  useEffect(() => {
    const generateQR = async () => {
      try {
        const dataUrl = await QRCode.toDataURL(url, {
          width: size * 2,
          margin: 2,
          color: {
            dark: '#1e293b',
            light: '#ffffff'
          }
        });
        setQrDataUrl(dataUrl);
      } catch (error) {
        console.error('生成二维码失败:', error);
      }
    };

    if (url) {
      generateQR();
    }
  }, [url, size]);

  if (!qrDataUrl) {
    return (
      <div className="download-float-qr-placeholder show">
        <div className="download-float-qr-placeholder-icon">🔄</div>
        <p>正在生成二维码...</p>
      </div>
    );
  }

  return (
    <img 
      src={qrDataUrl} 
      alt="下载二维码" 
      className="download-float-qr-image"
      style={{ width: size, height: size }}
    />
  );
};

const HomePage = () => {
  const [data, setData] = useState<any>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalContent, setModalContent] = useState('');
  const [activeStat, setActiveStat] = useState(0);
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [qrCodeInfo, setQrCodeInfo] = useState({ name: '', qrCodeUrl: '', tip: '' });
  const [currentProductIndex, setCurrentProductIndex] = useState(0);
  const [downloadModalVisible, setDownloadModalVisible] = useState(false);
  const [downloadInfo, setDownloadInfo] = useState<{
    productName: string;
    productIcon: string;
    platformName: string;
    downloadUrl: string;
    version?: string;
    size?: string;
  } | null>(null);
  const statsRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // 英雄区域展示产品列表
  const showcaseProducts = [
    {
      name: '云E助手',
      icon: '/images/adv/2.png',
      description: '在手机端查看socks5代理测试情况，实时掌握',
    },
    {
      name: '云投屏',
      icon: '/images/adv/10.jpg',
      description: '无线投屏协作工具',
    },
    {
      name: '云投屏',
      icon: '/images/adv/11.png',
      description: '无线投屏协作工具',
    },    
    {
      name: '云投屏',
      icon: '/images/adv/12.jpg',
      description: '无线投屏协作工具',
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get('/some-endpoint');
        setData(response.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    
    fetchData();
  }, []);

  // 数字滚动动画
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStat((prev) => (prev + 1) % stats.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  // 产品轮播动画
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(() => {
      setCurrentProductIndex((prev) => (prev + 1) % showcaseProducts.length);
    }, 4000);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [showcaseProducts.length]);

  // 统计数据
  const stats = [
    { value: '10+', label: '服务企业', icon: '🏢', isImage: false },
    { value: '2+', label: '软件著作权', icon: '/images/icons/certificate.png', isImage: true },
    { value: '98%', label: '系统稳定性', icon: '⚡', isImage: false },
    { value: '24/5', label: '技术支持', icon: '🔧', isImage: false },
  ];

  // 获取小程序二维码路径
  const getMiniProgramQRCode = (productId: string, platformKey: string) => {
    const qrCodes: Record<string, Record<string, { name: string; qrCodeUrl: string; tip: string }>> = {
      'ar-glasses': {
        wechat: {
          name: 'AR眼镜云服务 - 微信小程序',
          qrCodeUrl: '/images/qrcode/ar-glasses-wechat.png',
          tip: '微信扫码打开AR眼镜云服务小程序'
        },
        alipay: {
          name: 'AR眼镜云服务 - 支付宝小程序',
          qrCodeUrl: '/images/qrcode/ar-glasses-alipay.png',
          tip: '支付宝扫码打开AR眼镜云服务小程序'
        }
      },
      'cloud-cast': {
        wechat: {
          name: '云投屏APP - 微信小程序',
          qrCodeUrl: '/images/qrcode/cloud-cast-wechat.png',
          tip: '微信扫码打开云投屏小程序'
        },
        alipay: {
          name: '云投屏APP - 支付宝小程序',
          qrCodeUrl: '/images/qrcode/cloud-cast-alipay.png',
          tip: '支付宝扫码打开云投屏小程序'
        }
      },
      'cloud-assistant': {
        wechat: {
          name: '云E助手 - 微信小程序',
          qrCodeUrl: '/images/qrcode/cloud-assistant-wechat.png',
          tip: '微信扫码打开云E助手小程序'
        },
        alipay: {
          name: '云E助手 - 支付宝小程序',
          qrCodeUrl: '/images/qrcode/cloud-assistant-alipay.png',
          tip: '支付宝扫码打开云E助手小程序'
        }
      },
      'cloud-voice': {
        wechat: {
          name: '云短音通信软件 - 微信小程序',
          qrCodeUrl: '/images/qrcode/cloud-voice-wechat.png',
          tip: '微信扫码打开云短音通信软件小程序'
        },
        alipay: {
          name: '云短音通信软件 - 支付宝小程序',
          qrCodeUrl: '/images/qrcode/cloud-voice-alipay.png',
          tip: '支付宝扫码打开云短音通信软件小程序'
        }
      }
    };

    return qrCodes[productId]?.[platformKey] || {
      name: `${platformKey === 'wechat' ? '微信' : '支付宝'}小程序`,
      qrCodeUrl: `/images/qrcode/default-${platformKey}.png`,
      tip: `扫码打开小程序`
    };
  };

  // 检查平台是否支持下载
  const isPlatformAvailable = (productId: string, platformKey: string): boolean => {
    // 定义每个产品支持的平台
    const availablePlatforms: Record<string, string[]> = {
      'ar-glasses': ['official'],
      'cloud-cast': ['official', 'huawei','vivo'],
      'cloud-assistant': ['official', 'huawei', 'wechat'],
      'cloud-voice': ['windows'],
    };
    
    const available = availablePlatforms[productId] || [];
    return available.includes(platformKey);
  };

  // 处理下载点击
  const handleDownload = (product: any, platform: any) => {
    console.log('下载点击:', product.name, platform.name, platform.key);
    
    if (!isPlatformAvailable(product.id, platform.key)) {
      console.log(`${product.name} - ${platform.name} 暂不支持下载`);
      return;
    }

    // 微信小程序
    if (platform.key === 'wechat') {
      const qrInfo = getMiniProgramQRCode(product.id, 'wechat');
      setQrCodeInfo(qrInfo);
      setQrModalVisible(true);
      document.body.style.overflow = 'hidden';
      return;
    }
    
    // 支付宝小程序
    if (platform.key === 'alipay') {
      const qrInfo = getMiniProgramQRCode(product.id, 'alipay');
      setQrCodeInfo(qrInfo);
      setQrModalVisible(true);
      document.body.style.overflow = 'hidden';
      return;
    }
    
    // 官方下载 - 显示浮动层，包含自动生成的二维码和直接下载按钮
    if (platform.key === 'official') {
      console.log('打开官方下载浮动层');
      setDownloadInfo({
        productName: product.name,
        productIcon: productIcons[product.iconKey as keyof typeof productIcons] || productIcons.default,
        platformName: platform.name,
        downloadUrl: platform.url,
        version: getProductVersion(product.id),
        size: getProductSize(product.id),
      });
      setDownloadModalVisible(true);
      document.body.style.overflow = 'hidden';
      return;
    }
    
    // 其他平台直接下载
    if (platform.url) {
      window.open(platform.url, '_blank');
      console.log(`正在下载 ${product.name} - ${platform.name}，地址：${platform.url}`);
    }
  };

  // 获取产品版本信息
  const getProductVersion = (productId: string): string => {
    const versionMap: Record<string, string> = {
      'cloud-cast': 'v1.0.0.2316',
      'cloud-assistant': 'v2.1.0',
      'cloud-voice': 'v3.0.1',
    };
    return versionMap[productId] || 'v1.0.0';
  };

  // 获取产品大小信息
  const getProductSize = (productId: string): string => {
    const sizeMap: Record<string, string> = {
      'cloud-cast': '38.2 MB',
      'cloud-assistant': '0 MB',
      'cloud-voice': '0 MB',
    };
    return sizeMap[productId] || '未知';
  };

  // 处理直接下载
  const handleDirectDownload = () => {
    if (downloadInfo?.downloadUrl) {
      window.open(downloadInfo.downloadUrl, '_blank');
      console.log(`直接下载: ${downloadInfo.productName} - ${downloadInfo.downloadUrl}`);
      closeDownloadModal();
    }
  };

  // 关闭二维码弹窗
  const closeQrModal = () => {
    setQrModalVisible(false);
    document.body.style.overflow = 'auto';
  };

  // 关闭下载弹窗
  const closeDownloadModal = () => {
    setDownloadModalVisible(false);
    setDownloadInfo(null);
    document.body.style.overflow = 'auto';
  };

  // 下载产品数据
  const downloadCategories = [
    {
      id: 'app',
      name: 'APP产品',
      icon: '📱',
      description: '移动端应用，支持多平台下载',
      products: [
        {
          id: 'cloud-cast',
          name: '云投屏软件',
          iconKey: 'cloud-cast',
          description: '无线投屏协作工具，支持手机、平板等设备快速投屏，实现多屏互动，让演示和分享更加便捷高效（备注：应用商店产品名称为“云E投屏”）。',
          platforms: [
            { name: '官方下载', iconKey: 'official', url: 'https://g135.cn/app/dlna/lts/cscdlna_official_v1.0.0.2316_20260912_013021_lts.apk', key: 'official', available: true },
            { name: '华为应用商店', iconKey: 'huawei', url: '', key: 'huawei', available: true },
            { name: '小米应用商店', iconKey: 'xiaomi', url: '', key: 'xiaomi', available: true },
            { name: 'VIVO应用商店', iconKey: 'vivo', url: '', key: 'vivo', available: true },
            { name: '微信小程序', iconKey: 'wechat', url: '', key: 'wechat', available: true },
            { name: '支付宝小程序', iconKey: 'alipay', url: '', key: 'alipay', available: true }
          ]
        },
        {
          id: 'cloud-assistant',
          name: '云E助手',
          iconKey: 'cloud-assistant',
          description: '团队辅助工具，提供任务管理、日程安排、团队协作等功能，支持APP+小程序双端使用',
          platforms: [
            { name: '官方下载', iconKey: 'official', url: '', key: 'official', available: true },
            { name: '华为应用商店', iconKey: 'huawei', url: '', key: 'huawei', available: true },
            { name: '小米应用商店', iconKey: 'xiaomi', url: '', key: 'xiaomi', available: true },
            { name: 'VIVO应用商店', iconKey: 'vivo', url: '', key: 'vivo', available: true },
            { name: '微信小程序', iconKey: 'wechat', url: '', key: 'wechat', available: true },
            { name: '支付宝小程序', iconKey: 'alipay', url: '', key: 'alipay', available: true }
          ]
        }
      ]
    },
    {
      id: 'pc',
      name: 'PC产品',
      icon: '💻',
      description: '桌面端程序，支持主流操作系统',
      products: [
        {
          id: 'cloud-voice',
          name: '云短音通信软件',
          iconKey: 'cloud-voice',
          description: '短音通信核心组件，提供高效稳定的短消息通信服务，支持多种通信协议',
          platforms: [
            { name: '官方下载', iconKey: 'official', url: '', key: 'official', available: true },
            { name: 'Windows', iconKey: 'windows', url: '', key: 'windows', available: true },
            { name: 'Linux', iconKey: 'linux', url: '', key: 'linux', available: true }
          ]
        }
      ]
    }
  ];

  // 技术栈数据
  const techData: Record<string, { title: string; content: string }> = {
    language: {
      title: "开发语言",
      content: `
        <div class='tech-item'><span class='icon'>💻</span> C++</div>
        <div class='tech-item'><span class='icon'>⚙️</span> C语言</div>
        <div class='tech-item'><span class='icon'>🖥️</span> C# </div>
        <div class='tech-item'><span class='icon'>☕</span> Java</div>
        <div class='tech-item'><span class='icon'>🟨</span> JavaScript</div>
        <div class='tech-item'><span class='icon'>🔷</span> TypeScript</div>
        <div class='tech-item'><span class='icon'>📱</span> Kotlin</div>
      `
    },
    architecture: {
      title: "开发架构",
      content: `
        <div class='tech-item'><span class='icon'>🌐</span> B/S架构</div>
        <div class='tech-item'><span class='icon'>🖥️</span> C/S架构</div>
      `
    },
    tools: {
      title: "开发工具",
      content: `
        <div class='tech-item'><span class='icon'>🛠️</span> VS2019</div>
        <div class='tech-item'><span class='icon'>🧩</span> Qt</div>
        <div class='tech-item'><span class='icon'>📱</span> HBuildx</div>
        <div class='tech-item'><span class='icon'>🤖</span> Android Studio</div>
      `
    },
    model: {
      title: "建模工具",
      content: `
        <div class='tech-item'><span class='icon'>📐</span> Rose</div>
        <div class='tech-item'><span class='icon'>📊</span> EA</div>
        <div class='tech-item'><span class='icon'>🗂️</span> PD</div>
        <div class='tech-item'><span class='icon'>📋</span> AX</div>
      `
    },
    db: {
      title: "数据库",
      content: `
        <div class='tech-item'><span class='icon'>🗄️</span> MySQL</div>
        <div class='tech-item'><span class='icon'>🗃️</span> SQL Server</div>
        <div class='tech-item'><span class='icon'>📀</span> Oracle</div>
        <div class='tech-item'><span class='icon'>🔒</span> DM达梦</div>
        <div class='tech-item'><span class='icon'>⏱️</span> TDengine</div>
      `
    },
    version: {
      title: "版本管理",
      content: `
        <div class='tech-item'><span class='icon'>📌</span> SVN</div>
        <div class='tech-item'><span class='icon'>🌿</span> Git</div>
        <div class='tech-item'><span class='icon'>☁️</span> VSTS</div>
      `
    },
    os: {
      title: "操作系统",
      content: `
        <div class='tech-item'><span class='icon'>🪟</span> Windows</div>
        <div class='tech-item'><span class='icon'>🐉</span> 麒麟系统</div>
        <div class='tech-item'><span class='icon'>🐧</span> 深度系统</div>
        <div class='tech-item'><span class='icon'>🔴</span> RedHat</div>
        <div class='tech-item'><span class='icon'>⚫</span> CentOS</div>
      `
    },
    frontend: {
      title: "前端/移动端",
      content: `
        <div class='tech-item'><span class='icon'>⚛️</span> React</div>
        <div class='tech-item'><span class='icon'>📱</span> UniApp</div>
      `
    }
  };

  const showTech = (type: string) => {
    const data = techData[type];
    if (data) {
      setModalTitle(data.title);
      setModalContent(data.content);
      setModalVisible(true);
      document.body.style.overflow = 'hidden';
    }
  };

  const closeModal = () => {
    setModalVisible(false);
    document.body.style.overflow = 'auto';
  };

  // 核心产品数据
  const products = [
    {
      title: 'ERP系统',
      description: '企业资源计划管理系统，整合企业采购、生产、销售、财务、库存等核心业务',
      icon: '📊',
      color: '#2563eb'
    },
    {
      title: 'OA系统',
      description: '智能办公自动化系统，涵盖流程审批、公文管理、考勤管理、协同办公等功能',
      icon: '📋',
      color: '#7c3aed'
    },
    {
      title: '语音播放系统',
      description: '智能语音播报解决方案，支持自定义语音内容、定时播报、多场景适配',
      icon: '🔊',
      color: '#2563eb'
    },
    {
      title: 'CRM客服系统',
      description: '客户关系管理服务系统，整合客户信息、沟通记录、跟进进度等',
      icon: '🤝',
      color: '#7c3aed'
    },
    {
      title: 'SDR设备软件',
      description: '软件定义无线电配套软件，支持信号接收、解析、处理与传输',
      icon: '📡',
      color: '#2563eb'
    },
    {
      title: '云E助手',
      description: '团队辅助工具，同步支持APP与小程序双端，实时查看数据指标',
      icon: '☁️',
      color: '#7c3aed'
    },
    {
      title: 'Socks代理测试',
      description: '专业的SOCKS代理测试工具，支持多种代理协议检测，提供稳定可靠的代理服务测试环境',
      icon: '🌐',
      color: '#1890ff',
      isOnline: true,
      externalUrl: 'http://socks5.g135.cn'
    }
  ];

  const advantages = [
    {
      title: '技术领先',
      description: '拥有多项核心技术专利，持续投入研发创新',
      icon: '🚀'
    },
    {
      title: '安全可靠',
      description: '多层安全防护体系，保障数据安全与系统稳定',
      icon: '🛡️'
    },
    {
      title: '快速响应',
      description: '24/5技术支持团队，快速响应客户需求',
      icon: '⚡'
    },
    {
      title: '定制开发',
      description: '灵活满足个性化需求，提供全方位解决方案',
      icon: '🎯'
    }
  ];

  // 手动切换产品
  const handleIndicatorClick = (index: number) => {
    setCurrentProductIndex(index);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        setCurrentProductIndex((prev) => (prev + 1) % showcaseProducts.length);
      }, 4000);
    }
  };

  return (
    <div className="home-page">
      <section className="hero">
        <div className="hero-content">
          <h1 className="hero-title">
            用技术创新
            <br />
            <span className="gradient-text">驱动企业数字化转型</span>
          </h1>
          <p className="hero-description">
            专注于多领域软件研发与技术创新，为企业提供全方位数字化解决方案，
            助力企业实现智能化升级与业务增长
          </p>
          <div className="hero-buttons">
            <a href="#product" className="hero-btn primary">查看产品</a>
          </div>
        </div>
        <div className="hero-visual">
          <div className="product-showcase">
            <div className="product-image-wrapper">
              <div className="product-image-container">
                <img 
                  src={showcaseProducts[currentProductIndex].icon}
                  alt={showcaseProducts[currentProductIndex].name}
                  className="product-display-image"
                />
              </div>
              <div className="product-info">
                <h3 className="product-name">{showcaseProducts[currentProductIndex].name}</h3>
                <p className="product-description">{showcaseProducts[currentProductIndex].description}</p>
              </div>
              <div className="product-indicators">
                {showcaseProducts.map((_, idx) => (
                  <span 
                    key={idx} 
                    className={`product-dot ${currentProductIndex === idx ? 'active' : ''}`}
                    onClick={() => handleIndicatorClick(idx)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="download-section">
        <div className="download-container">
          <div className="section-header">
            <h2 className="section-title">产品下载</h2>
            <p className="section-subtitle">
              选择您需要的产品，点击对应平台进行下载
            </p>
          </div>
          {downloadCategories.map(category => (
            <div key={category.id} className="download-category">
              <div className="category-header">
                <div className="category-icon">
                  <span className="category-icon-emoji">{category.icon}</span>
                </div>
                <div>
                  <h3 className="category-title">{category.name}</h3>
                  <p className="category-desc">{category.description}</p>
                </div>
              </div>
              <div className="products-list">
                {category.products.map(product => {
                  // 过滤掉不可用的平台（即将上线的）
                  const availablePlatforms = product.platforms.filter(platform => 
                    isPlatformAvailable(product.id, platform.key)
                  );
                  
                  return (
                    <div key={product.id} className="product-download-item">
                      {/* 左侧：产品图片和名称 */}
                      <div className="product-left">
                        <div className="product-avatar">
                          <IconImage 
                            src={productIcons[product.iconKey as keyof typeof productIcons] || productIcons.default} 
                            alt={product.name} 
                            className="product-avatar-img"
                            fallbackIcon="📦"
                          />
                        </div>
                        <div className="product-title">{product.name}</div>
                      </div>
                      
                      {/* 右侧：上下两行布局 */}
                      <div className="product-right">
                        {/* 上一行：产品描述 */}
                        <div className="product-description-row">
                          <p className="product-description-text">{product.description}</p>
                        </div>
                        
                        {/* 下一行：所有下载渠道（只显示可用的） */}
                        {availablePlatforms.length > 0 && (
                          <div className="product-platforms-row">
                            <div className="platforms-label">下载渠道：</div>
                            <div className="platforms-buttons">
                              {availablePlatforms.map(platform => (
                                <button
                                  key={platform.name}
                                  className="platform-btn"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleDownload(product, platform);
                                  }}
                                  onTouchEnd={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleDownload(product, platform);
                                  }}
                                >
                                  <IconImage 
                                    src={platformIcons[platform.iconKey as keyof typeof platformIcons] || platformIcons.default} 
                                    alt={platform.name} 
                                    className="platform-icon-img"
                                    fallbackIcon="📎"
                                  />
                                  <span>{platform.name}</span>
                                  <svg className="download-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                    <polyline points="7 10 12 15 17 10"/>
                                    <line x1="12" y1="15" x2="12" y2="3"/>
                                  </svg>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="stats-section">
        <div className="stats-container" ref={statsRef}>
          {stats.map((stat, index) => (
            <div 
              key={index} 
              className={`stat-item ${activeStat === index ? 'active' : ''}`}
            >
              {stat.isImage ? (
                <img src={stat.icon} alt={stat.label} className="stat-icon-img" />
              ) : (
                <span className="stat-icon">{stat.icon}</span>
              )}
              <div className="stat-value">{stat.value}</div>
              <div className="stat-label">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="section" id="advantages">
        <div className="section-header">
          <h2 className="section-title">为什么选择我们</h2>
          <p className="section-subtitle">
            以技术创新为核心，为企业提供全方位、高质量的技术服务
          </p>
        </div>
        <div className="advantages-grid">
          {advantages.map((advantage, index) => (
            <div key={index} className="advantage-card">
              <div className="advantage-icon">{advantage.icon}</div>
              <h3>{advantage.title}</h3>
              <p>{advantage.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="section section-alt" id="tech">
        <div className="section-header">
          <h2 className="section-title">核心技术栈</h2>
          <p className="section-subtitle">
            全面且成熟的技术体系，覆盖软件开发全流程
          </p>
        </div>
        <div className="tech-stack">
          <div className="tech-card" onClick={() => showTech('language')}>
            <div className="tech-card-icon">💻</div>
            <h3>开发语言</h3>
            <p>高效稳定的主流编程语言，适配不同架构需求</p>
            <span className="tech-card-link">了解更多 →</span>
          </div>
          <div className="tech-card" onClick={() => showTech('architecture')}>
            <div className="tech-card-icon">🏗️</div>
            <h3>开发架构</h3>
            <p>灵活架构模式，适配不同业务场景</p>
            <span className="tech-card-link">了解更多 →</span>
          </div>
          <div className="tech-card" onClick={() => showTech('tools')}>
            <div className="tech-card-icon">🛠️</div>
            <h3>开发工具</h3>
            <p>行业主流工具，提升研发效率</p>
            <span className="tech-card-link">了解更多 →</span>
          </div>
          <div className="tech-card" onClick={() => showTech('db')}>
            <div className="tech-card-icon">🗄️</div>
            <h3>数据库</h3>
            <p>多类型数据库支持，保障数据安全</p>
            <span className="tech-card-link">了解更多 →</span>
          </div>
          <div className="tech-card" onClick={() => showTech('frontend')}>
            <div className="tech-card-icon">⚛️</div>
            <h3>前端/移动端</h3>
            <p>流畅美观的交互体验，多终端适配</p>
            <span className="tech-card-link">了解更多 →</span>
          </div>
          <div className="tech-card" onClick={() => showTech('os')}>
            <div className="tech-card-icon">🖥️</div>
            <h3>操作系统</h3>
            <p>多系统环境支持，满足国产化需求</p>
            <span className="tech-card-link">了解更多 →</span>
          </div>
        </div>
      </section>

      <section className="section" id="product">
        <div className="section-header">
          <h2 className="section-title">核心产品</h2>
          <p className="section-subtitle">
            覆盖多个业务领域，为企业提供全面的数字化解决方案
          </p>
        </div>
        <div className="products-grid">
          {products.map((product, index) => (
            <div 
              key={index} 
              className="product-card"
              onClick={() => {
                if (product.isOnline && product.externalUrl) {
                  window.open(product.externalUrl, '_blank');
                }
              }}
              style={{ cursor: product.isOnline ? 'pointer' : 'default' }}
            >
              <div 
                className="product-card-accent"
                style={{ background: product.color }}
              ></div>
              <div className="product-card-content">
                <span className="product-icon">{product.icon}</span>
                <h3>
                  {product.title}
                  {product.isOnline && <span className="online-badge">在线</span>}
                </h3>
                <p>{product.description}</p>
                {product.isOnline && (
                  <div className="product-link">
                    立即使用 <span className="arrow">→</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="section section-gradient" id="research">
        <div className="research-content">
          <h2>持续创新，探索科技前沿</h2>
          <p>
            聚焦多领域技术研发，涵盖应用开发、嵌入式、物联网等多个方向，
            不断拓展研发边界，推动行业技术升级
          </p>
          <div className="research-tags">
            <span>应用开发</span>
            <span>嵌入式系统</span>
            <span>物联网</span>
            <span>人工智能</span>
            <span>大数据</span>
            <span>云计算</span>
          </div>
        </div>
      </section>

      {/* 技术栈弹窗 */}
      {modalVisible && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="tech-modal" onClick={(e) => e.stopPropagation()}>
            <span className="modal-close" onClick={closeModal}>&times;</span>
            <h2 className="modal-title">{modalTitle}</h2>
            <div className="modal-content" dangerouslySetInnerHTML={{ __html: modalContent }}></div>
          </div>
        </div>
      )}

      {/* 小程序二维码弹窗 */}
      {qrModalVisible && (
        <div className="modal-overlay" onClick={closeQrModal}>
          <div className="qr-modal" onClick={(e) => e.stopPropagation()}>
            <span className="modal-close" onClick={closeQrModal}>&times;</span>
            <div className="qr-modal-content">
              <div className="qr-icon">📱</div>
              <h3 className="qr-title">{qrCodeInfo.name}</h3>
              <div className="qr-code-container">
                <img 
                  src={qrCodeInfo.qrCodeUrl} 
                  alt={qrCodeInfo.name}
                  className="qr-code-image"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const placeholder = e.currentTarget.nextElementSibling;
                    if (placeholder) placeholder.classList.add('show');
                  }}
                />
                <div className="qr-placeholder">
                  <div className="qr-placeholder-icon">📲</div>
                  <p>请将二维码图片放置于<br />{qrCodeInfo.qrCodeUrl} 路径</p>
                </div>
              </div>
              <p className="qr-tip">{qrCodeInfo.tip}</p>
            </div>
          </div>
        </div>
      )}

      {/* 官方下载浮动层 */}
      {downloadModalVisible && downloadInfo && (
        <div className="modal-overlay" onClick={closeDownloadModal}>
          <div className="download-float-modal" onClick={(e) => e.stopPropagation()}>
            <span className="modal-close" onClick={closeDownloadModal}>&times;</span>
            
            <div className="download-float-header">
              <div className="download-float-product-icon">
                <img 
                  src={downloadInfo.productIcon} 
                  alt={downloadInfo.productName}
                  className="download-float-icon-img"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
              <div className="download-float-product-info">
                <h3 className="download-float-product-name">{downloadInfo.productName}</h3>
                <div className="download-float-product-meta">
                  <span className="download-float-version">{downloadInfo.version}</span>
                  <span className="download-float-divider">|</span>
                  <span className="download-float-size">{downloadInfo.size}</span>
                </div>
              </div>
            </div>

            <div className="download-float-body">
              <div className="download-float-qr-section">
                <div className="download-float-qr-wrapper">
                  <AutoQRCode url={downloadInfo.downloadUrl} size={140} />
                </div>
                <p className="download-float-qr-tip">使用手机扫描二维码下载</p>
              </div>

              <div className="download-float-divider-section">
                <span className="download-float-divider-text">或</span>
              </div>

              <div className="download-float-direct-section">
                <button 
                  className="download-float-direct-btn"
                  onClick={handleDirectDownload}
                >
                  <svg className="download-float-direct-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  直接下载
                </button>
                <p className="download-float-direct-tip">点击按钮直接下载到电脑</p>
              </div>
            </div>

            <div className="download-float-footer">
              <p className="download-float-footer-text">
                支持 Android / iOS / Windows / macOS 多平台
              </p>
            </div>
          </div>
        </div>
      )}

      <style>{`
        :root {
          --primary: #2563eb;
          --primary-light: #3b82f6;
          --secondary: #7c3aed;
          --bg: #f0f7ff;
          --card-bg: #ffffff;
          --text: #1e293b;
          --text-light: #64748b;
          --text-disabled: #94a3b8;
          --border: rgba(37, 99, 235, 0.15);
          --border-disabled: rgba(100, 116, 139, 0.15);
          --gradient: linear-gradient(135deg, #2563eb, #7c3aed);
          --glow: 0 0 20px rgba(37, 99, 235, 0.3);
        }

        .home-page {
          background: var(--bg);
          color: var(--text);
          overflow-x: hidden;
          position: relative;
          font-family: 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        }

        /* 英雄区域 - 全屏融合设计 */
        .hero {
          min-height: calc(100vh - 70px);
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 8%;
          position: relative;
          z-index: 1;
          gap: 60px;
        }

        .hero-content {
          flex: 0.9;
          max-width: 500px;
          position: relative;
          z-index: 2;
        }

        .hero-title {
          font-size: 52px;
          font-weight: 800;
          line-height: 1.2;
          margin-bottom: 24px;
          color: var(--text);
        }

        .gradient-text {
          background: var(--gradient);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .hero-description {
          font-size: 16px;
          line-height: 1.8;
          color: var(--text-light);
          margin-bottom: 32px;
        }

        .hero-buttons {
          display: flex;
          gap: 16px;
        }

        .hero-btn {
          padding: 12px 28px;
          border-radius: 30px;
          font-size: 14px;
          font-weight: 500;
          text-decoration: none;
          transition: all 0.3s;
        }

        .hero-btn.primary {
          background: var(--gradient);
          color: white;
          box-shadow: 0 4px 15px rgba(37, 99, 235, 0.3);
        }

        .hero-btn.primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(37, 99, 235, 0.4);
        }

        .hero-visual {
          flex: 1.1;
          display: flex;
          justify-content: center;
          align-items: center;
          position: relative;
          z-index: 2;
        }

        .product-showcase {
          width: 100%;
          max-width: 600px;
        }

        .product-image-wrapper {
          position: relative;
          background: transparent;
          border-radius: 32px;
        }

        .product-image-wrapper::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: radial-gradient(circle at center, transparent 70%, rgba(240, 247, 255, 0.15) 100%);
          pointer-events: none;
          z-index: 1;
          border-radius: 32px;
        }

        .product-image-wrapper::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          box-shadow: inset 0 0 20px rgba(240, 247, 255, 0.3);
          pointer-events: none;
          z-index: 1;
          border-radius: 32px;
        }

        .product-image-container {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          border-radius: 5px;
          overflow: hidden;
        }

        .product-display-image {
          width: 100%;
          height: auto;
          max-height: 520px;
          object-fit: contain;
          transition: opacity 0.3s ease, transform 0.5s ease;
          animation: fadeInScale 0.6s ease;
          filter: drop-shadow(0 10px 30px rgba(0, 0, 0, 0.1));
        }

        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .product-info {
          padding: 28px 0 20px;
          text-align: center;
        }

        .product-name {
          font-size: 28px;
          font-weight: 700;
          color: var(--text);
          margin-bottom: 12px;
        }

        .product-description {
          font-size: 15px;
          color: var(--text-light);
          line-height: 1.7;
          max-width: 90%;
          margin: 0 auto;
        }

        .product-indicators {
          display: flex;
          gap: 12px;
          justify-content: center;
          padding-top: 12px;
        }

        .product-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background: #ddd;
          cursor: pointer;
          transition: all 0.3s;
        }

        .product-dot.active {
          width: 32px;
          border-radius: 5px;
          background: var(--primary);
        }

        .product-dot:hover {
          background: var(--primary-light);
        }

        /* 产品下载区域样式 */
        .download-section {
          padding: 60px 8%;
          position: relative;
          z-index: 1;
          background: rgba(37, 99, 235, 0.02);
        }

        .download-container {
          max-width: 1400px;
          margin: 0 auto;
        }

        .download-category {
          margin-bottom: 48px;
        }

        .download-category:last-child {
          margin-bottom: 0;
        }

        .category-header {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 24px;
          padding-bottom: 16px;
          border-bottom: 2px solid var(--border);
        }

        .category-icon {
          width: 56px;
          height: 56px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .category-icon-emoji {
          font-size: 40px;
        }

        .category-title {
          font-size: 24px;
          font-weight: 700;
          color: var(--text);
          margin: 0 0 4px;
        }

        .category-desc {
          font-size: 14px;
          color: var(--text-light);
          margin: 0;
        }

        /* 产品列表 */
        .products-list {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        /* 每个产品的容器 - 水平布局 */
        .product-download-item {
          display: flex;
          align-items: stretch;
          background: var(--card-bg);
          border: 1px solid var(--border);
          border-radius: 16px;
          overflow: hidden;
          transition: all 0.3s;
          min-height: 140px;
        }

        .product-download-item:hover {
          border-color: var(--primary);
          box-shadow: 0 4px 20px rgba(37, 99, 235, 0.1);
          transform: translateY(-2px);
        }

        /* 左侧：产品图片和名称区域 */
        .product-left {
          flex: 0 0 200px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 12px;
          padding: 20px 16px;
          background: linear-gradient(135deg, rgba(37, 99, 235, 0.03), rgba(124, 58, 237, 0.03));
          border-right: 1px solid var(--border);
        }

        .product-avatar {
          width: 64px;
          height: 64px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .product-avatar-img {
          width: 56px;
          height: 56px;
          object-fit: contain;
        }

        .product-title {
          font-size: 16px;
          font-weight: 700;
          color: var(--text);
          text-align: center;
          line-height: 1.3;
        }

        /* 右侧：上下两行布局 */
        .product-right {
          flex: 1;
          display: flex;
          flex-direction: column;
          padding: 16px 20px;
          gap: 12px;
        }

        /* 上一行：产品描述 */
        .product-description-row {
          flex: 1;
          display: flex;
          align-items: flex-start;
        }

        .product-description-text {
          font-size: 13px;
          color: var(--text-light);
          line-height: 1.6;
          margin: 0;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        /* 下一行：下载渠道 */
        .product-platforms-row {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
          padding-top: 8px;
          border-top: 1px solid var(--border);
        }

        .platforms-label {
          font-size: 12px;
          font-weight: 600;
          color: var(--text-light);
          white-space: nowrap;
        }

        .platforms-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          flex: 1;
        }

        /* 平台按钮样式 */
        .platform-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 14px;
          border-radius: 32px;
          text-decoration: none;
          color: var(--text);
          font-size: 12px;
          font-weight: 500;
          transition: all 0.25s;
          border: 1px solid var(--border);
          background: var(--bg);
          cursor: pointer;
          white-space: nowrap;
          outline: none;
          -webkit-tap-highlight-color: transparent;
          touch-action: manipulation;
        }

        .platform-btn:hover {
          background: var(--primary);
          color: white;
          border-color: var(--primary);
          transform: translateY(-2px);
          box-shadow: 0 2px 8px rgba(37, 99, 235, 0.2);
        }

        .platform-btn:active {
          transform: translateY(0);
          box-shadow: 0 1px 4px rgba(37, 99, 235, 0.3);
        }

        .platform-icon-img {
          width: 16px;
          height: 16px;
          object-fit: contain;
        }

        .download-icon {
          width: 12px;
          height: 12px;
          opacity: 0.5;
          transition: opacity 0.2s;
        }

        .platform-btn:hover .download-icon {
          opacity: 1;
        }

        /* ============ 官方下载浮动层样式 ============ */
        .download-float-modal {
          width: 90%;
          max-width: 500px;
          background: #ffffff;
          border-radius: 24px;
          padding: 0;
          position: relative;
          animation: modalPop 0.4s ease;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
          max-height: 90vh;
          overflow-y: auto;
        }

        .download-float-header {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 28px 32px 20px;
          background: linear-gradient(135deg, rgba(37, 99, 235, 0.05), rgba(124, 58, 237, 0.05));
          border-bottom: 1px solid var(--border);
        }

        .download-float-product-icon {
          width: 56px;
          height: 56px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: white;
          border-radius: 16px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        }

        .download-float-icon-img {
          width: 40px;
          height: 40px;
          object-fit: contain;
        }

        .download-float-product-info {
          flex: 1;
        }

        .download-float-product-name {
          font-size: 20px;
          font-weight: 700;
          color: var(--text);
          margin: 0 0 6px;
        }

        .download-float-product-meta {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .download-float-version {
          font-size: 13px;
          color: var(--primary);
          font-weight: 500;
          background: rgba(37, 99, 235, 0.1);
          padding: 2px 8px;
          border-radius: 12px;
        }

        .download-float-divider {
          color: var(--text-disabled);
        }

        .download-float-size {
          font-size: 13px;
          color: var(--text-light);
        }

        .download-float-body {
          display: flex;
          align-items: center;
          padding: 32px;
          gap: 24px;
        }

        .download-float-qr-section {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
        }

        .download-float-qr-wrapper {
          background: white;
          padding: 12px;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .download-float-qr-image {
          display: block;
        }

        .download-float-qr-placeholder {
          width: 140px;
          height: 140px;
          background: #f8fafc;
          border-radius: 12px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }

        .download-float-qr-placeholder.show {
          display: flex;
        }

        .download-float-qr-placeholder-icon {
          font-size: 36px;
          margin-bottom: 8px;
          opacity: 0.5;
        }

        .download-float-qr-placeholder p {
          font-size: 11px;
          color: var(--text-light);
          margin: 0;
          text-align: center;
          line-height: 1.4;
        }

        .download-float-qr-tip {
          font-size: 13px;
          color: var(--text-light);
          margin: 0;
          text-align: center;
        }

        .download-float-divider-section {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 8px;
        }

        .download-float-divider-text {
          font-size: 14px;
          color: var(--text-disabled);
          font-weight: 500;
          position: relative;
        }

        .download-float-divider-text::before,
        .download-float-divider-text::after {
          display: none;
        }

        .download-float-direct-section {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
        }

        .download-float-direct-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 14px 32px;
          background: var(--gradient);
          color: white;
          border: none;
          border-radius: 32px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
          box-shadow: 0 4px 15px rgba(37, 99, 235, 0.3);
          -webkit-tap-highlight-color: transparent;
          touch-action: manipulation;
        }

        .download-float-direct-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(37, 99, 235, 0.4);
        }

        .download-float-direct-btn:active {
          transform: translateY(0);
          box-shadow: 0 2px 10px rgba(37, 99, 235, 0.3);
        }

        .download-float-direct-icon {
          width: 18px;
          height: 18px;
        }

        .download-float-direct-tip {
          font-size: 13px;
          color: var(--text-light);
          margin: 0;
          text-align: center;
        }

        .download-float-footer {
          padding: 16px 32px;
          background: rgba(37, 99, 235, 0.03);
          border-top: 1px solid var(--border);
        }

        .download-float-footer-text {
          font-size: 12px;
          color: var(--text-disabled);
          margin: 0;
          text-align: center;
        }

        /* ============ 其他样式保持不变 ============ */
        .stats-section {
          padding: 60px 8%;
          position: relative;
          z-index: 1;
        }

        .stats-container {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 24px;
          max-width: 1000px;
          margin: 0 auto;
        }

        .stat-item {
          text-align: center;
          padding: 30px 20px;
          background: var(--card-bg);
          border-radius: 12px;
          border: 1px solid var(--border);
          transition: all 0.3s;
        }

        .stat-item:hover,
        .stat-item.active {
          border-color: var(--primary);
          box-shadow: var(--glow);
          transform: translateY(-4px);
        }

        .stat-icon {
          font-size: 32px;
          margin-bottom: 12px;
          display: block;
        }

        .stat-icon-img {
          width: 48px;
          height: 48px;
          margin: 0 auto 12px;
          display: block;
          object-fit: contain;
        }

        .stat-value {
          font-size: 32px;
          font-weight: 800;
          background: var(--gradient);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .stat-label {
          font-size: 14px;
          color: var(--text-light);
          margin-top: 4px;
        }

        .section {
          padding: 100px 8%;
          position: relative;
          z-index: 1;
        }

        .section-alt {
          background: rgba(37, 99, 235, 0.03);
        }

        .section-header {
          text-align: center;
          margin-bottom: 60px;
        }

        .section-title {
          font-size: 40px;
          font-weight: 800;
          margin-bottom: 16px;
        }

        .section-subtitle {
          font-size: 18px;
          color: var(--text-light);
          max-width: 600px;
          margin: 0 auto;
          line-height: 1.8;
        }

        .advantages-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 24px;
          max-width: 1100px;
          margin: 0 auto;
        }

        .advantage-card {
          text-align: center;
          padding: 40px 24px;
          background: var(--card-bg);
          border-radius: 12px;
          border: 1px solid var(--border);
          transition: all 0.3s;
        }

        .advantage-card:hover {
          border-color: var(--primary);
          transform: translateY(-8px);
          box-shadow: var(--glow);
        }

        .advantage-icon {
          font-size: 48px;
          margin-bottom: 16px;
        }

        .advantage-card h3 {
          font-size: 20px;
          margin-bottom: 12px;
        }

        .advantage-card p {
          color: var(--text-light);
          line-height: 1.6;
          font-size: 15px;
        }

        .tech-stack {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
          max-width: 1100px;
          margin: 0 auto;
        }

        .tech-card {
          background: var(--card-bg);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 32px 24px;
          transition: all 0.3s;
          cursor: pointer;
        }

        .tech-card:hover {
          border-color: var(--primary);
          transform: translateY(-8px);
          box-shadow: 0 2px 12px rgba(37, 99, 235, 0.15);
        }

        .tech-card-icon {
          font-size: 40px;
          margin-bottom: 16px;
        }

        .tech-card h3 {
          font-size: 20px;
          margin-bottom: 8px;
        }

        .tech-card p {
          color: var(--text-light);
          font-size: 15px;
          line-height: 1.6;
        }

        .tech-card-link {
          display: inline-block;
          margin-top: 12px;
          color: var(--primary);
          font-size: 14px;
          font-weight: 500;
        }

        .products-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
          max-width: 1100px;
          margin: 0 auto;
        }

        .product-card {
          background: var(--card-bg);
          border-radius: 12px;
          overflow: hidden;
          transition: all 0.3s;
          border: 1px solid var(--border);
        }

        .product-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 8px 25px rgba(24, 144, 255, 0.15);
        }

        .product-card-accent {
          height: 6px;
        }

        .product-card-content {
          padding: 28px;
        }

        .product-icon {
          font-size: 36px;
          margin-bottom: 12px;
          display: block;
        }

        .product-card h3 {
          font-size: 20px;
          margin-bottom: 10px;
        }

        .product-card p {
          color: var(--text-light);
          font-size: 14px;
          line-height: 1.7;
        }

        .online-badge {
          display: inline-block;
          background: linear-gradient(135deg, #1890ff, #52c41a);
          color: white;
          font-size: 10px;
          padding: 2px 8px;
          border-radius: 12px;
          margin-left: 8px;
          vertical-align: middle;
          font-weight: normal;
        }

        .product-link {
          margin-top: 16px;
          color: #1890ff;
          font-size: 13px;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 4px;
          transition: all 0.3s ease;
        }

        .product-link .arrow {
          transition: transform 0.3s ease;
        }

        .product-card:hover .product-link .arrow {
          transform: translateX(4px);
        }

        .section-gradient {
          background: var(--gradient);
          color: white;
          text-align: center;
          padding: 80px 8%;
        }

        .research-content {
          max-width: 800px;
          margin: 0 auto;
        }

        .research-content h2 {
          font-size: 36px;
          font-weight: 800;
          margin-bottom: 20px;
        }

        .research-content p {
          font-size: 18px;
          line-height: 1.8;
          opacity: 0.9;
          margin-bottom: 36px;
        }

        .research-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          justify-content: center;
        }

        .research-tags span {
          padding: 10px 24px;
          background: rgba(255, 255, 255, 0.15);
          border-radius: 50px;
          font-size: 15px;
          backdrop-filter: blur(10px);
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(15, 23, 42, 0.6);
          backdrop-filter: blur(6px);
          z-index: 9999;
          display: flex;
          justify-content: center;
          align-items: center;
          animation: fadeIn 0.3s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .tech-modal {
          width: 90%;
          max-width: 550px;
          background: #ffffff;
          border-radius: 16px;
          padding: 48px 40px;
          position: relative;
          animation: modalPop 0.4s ease;
          border: 2px solid rgba(37, 99, 235, 0.3);
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
        }

        .qr-modal {
          width: 90%;
          max-width: 400px;
          background: #ffffff;
          border-radius: 24px;
          padding: 32px 28px;
          position: relative;
          animation: modalPop 0.4s ease;
          text-align: center;
        }

        .qr-modal-content {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .qr-icon {
          font-size: 48px;
          margin-bottom: 8px;
        }

        .qr-title {
          font-size: 22px;
          font-weight: 700;
          color: var(--text);
          margin: 8px 0 20px;
        }

        .qr-code-container {
          background: white;
          padding: 16px;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          margin-bottom: 16px;
        }

        .qr-code-image {
          width: 200px;
          height: 200px;
          display: block;
          margin: 0 auto;
        }

        .qr-placeholder {
          width: 200px;
          height: 200px;
          background: #f5f5f5;
          border-radius: 12px;
          display: none;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
        }

        .qr-placeholder.show {
          display: flex;
        }

        .qr-placeholder-icon {
          font-size: 48px;
          margin-bottom: 12px;
          opacity: 0.5;
        }

        .qr-placeholder p {
          font-size: 12px;
          color: var(--text-light);
          margin: 0;
        }

        .qr-tip {
          font-size: 14px;
          color: var(--text-light);
          margin: 8px 0 0;
        }

        @keyframes modalPop {
          from { opacity: 0; transform: scale(0.9) translateY(20px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }

        .modal-close {
          position: absolute;
          top: 16px;
          right: 20px;
          font-size: 28px;
          color: var(--primary);
          cursor: pointer;
          transition: 0.3s;
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          background: transparent;
          line-height: 1;
          z-index: 10;
        }

        .modal-close:hover {
          color: var(--text);
          background: #f1f5f9;
        }

        .modal-title {
          text-align: center;
          font-size: 28px;
          font-weight: 700;
          margin-bottom: 32px;
          color: var(--primary);
        }

        .modal-content {
          text-align: left;
          font-size: 18px;
          line-height: 2.6;
          color: var(--text);
        }

        .tech-item {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          gap: 12px;
          margin: 6px 0;
        }

        .tech-item .icon {
          font-size: 22px;
          color: var(--primary);
        }

        /* 响应式设计 */
        @media (max-width: 1024px) {
          .hero {
            flex-direction: column;
            text-align: center;
            padding: 60px 5%;
            gap: 40px;
          }
          .hero-content { max-width: 100%; }
          .hero-buttons { justify-content: center; }
          .hero-title { font-size: 42px; }
          .product-showcase { max-width: 450px; margin: 0 auto; }
          .product-display-image { max-height: 400px; }
          .stats-container { grid-template-columns: repeat(2, 1fr); }
          .advantages-grid { grid-template-columns: repeat(2, 1fr); }
          .tech-stack { grid-template-columns: repeat(2, 1fr); }
          .products-grid { grid-template-columns: repeat(2, 1fr); }
          
          .product-download-item {
            flex-direction: column;
          }
          .product-left {
            flex-direction: row;
            flex: auto;
            border-right: none;
            border-bottom: 1px solid var(--border);
            padding: 16px;
            gap: 16px;
          }
          .product-avatar {
            width: 48px;
            height: 48px;
          }
          .product-avatar-img {
            width: 40px;
            height: 40px;
          }
          .product-title {
            font-size: 15px;
          }
          
          .download-float-body {
            flex-direction: column;
            padding: 24px;
          }
          .download-float-divider-section {
            padding: 8px 0;
          }
        }

        @media (max-width: 640px) {
          .hero { padding: 40px 5%; }
          .hero-title { font-size: 32px; }
          .hero-description { font-size: 16px; }
          .section-title { font-size: 28px; }
          .product-showcase { max-width: 100%; }
          .product-display-image { max-height: 320px; }
          .product-name { font-size: 22px; }
          .product-description { font-size: 13px; }
          .stats-container { grid-template-columns: repeat(2, 1fr); gap: 12px; }
          .stat-item { padding: 20px 12px; }
          .stat-value { font-size: 24px; }
          .advantages-grid { grid-template-columns: 1fr; }
          .tech-stack { grid-template-columns: 1fr; }
          .products-grid { grid-template-columns: 1fr; }
          .section { padding: 60px 5%; }
          .tech-modal { padding: 30px 20px; }
          .modal-title { font-size: 22px; }
          .research-content h2 { font-size: 28px; }
          .category-header { flex-direction: column; text-align: center; }
          .category-title { font-size: 20px; }
          .qr-modal { padding: 24px 20px; }
          .qr-code-image, .qr-placeholder { width: 160px; height: 160px; }
          .stat-icon-img { width: 40px; height: 40px; }
          .product-image-wrapper::before,
          .product-image-wrapper::after {
            display: none;
          }
          
          .product-left {
            padding: 12px;
          }
          .product-avatar {
            width: 40px;
            height: 40px;
          }
          .product-avatar-img {
            width: 36px;
            height: 36px;
          }
          .product-title {
            font-size: 14px;
          }
          .product-description-text {
            font-size: 12px;
          }
          .platform-btn {
            padding: 8px 12px;
            font-size: 11px;
            min-height: 36px;
          }
          .platforms-label {
            font-size: 11px;
          }
          .platforms-buttons {
            gap: 6px;
          }
          
          .download-float-modal {
            max-width: 95%;
            margin: 0 10px;
          }
          .download-float-header {
            padding: 20px 16px;
            gap: 12px;
          }
          .download-float-product-icon {
            width: 44px;
            height: 44px;
          }
          .download-float-icon-img {
            width: 32px;
            height: 32px;
          }
          .download-float-product-name {
            font-size: 16px;
          }
          .download-float-body {
            padding: 20px 16px;
          }
          .download-float-direct-btn {
            padding: 12px 24px;
            font-size: 14px;
          }
        }
      `}</style>
    </div>
  );
};

export default HomePage;